import { handleApiResult } from '@store/error';
import { apiSlice } from './apiSlice';
import { type IProject } from './projectEndpoints';

export interface IFolder {
  _id: string;
  name: string;
  path: string;
  parentFolder?: string | IFolder | null;
  children: IFolderChild[];
  createdAt: string;
  updatedAt: string;
}

export const folderChildTypes = ['Folder', 'Project'] as const;

export type FolderChildType = typeof folderChildTypes[number];

export interface IFolderChild { type: FolderChildType, item: string | IFolder | IProject }

export function isFolder(child: IFolderChild): child is { type: 'Folder'; item: IFolder } {
  return child.type === 'Folder';
}

export function isProject(child: IFolderChild): child is { type: 'Project'; item: IProject } {
  return child.type === 'Project';
}

export const projectApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getFolders: builder.query<IFolder[], void>({
      query: () => '/v1/folders',
      providesTags: (result = []) => [
        ...result.map(({ _id }) => ({ type: 'Folder' as const, id: _id ?? 'root' })),
        { type: 'Folder', id: 'LIST' },
      ],
      onQueryStarted: handleApiResult(),
    }),
    getFolder: builder.query<IFolder, string>({
      query: (id) => `/v1/folders/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Folder', id: id ?? 'root' }],
      onQueryStarted: handleApiResult(),
    }),
    getFolderByPath: builder.query<IFolder, string>({
      query: (path) => `/v1/folders?path=${encodeURIComponent(path)}`,
      providesTags: (result) => {
        const tags: ({ type: 'Folder' | 'Project'; id: string })[] = result
          ? [{ type: 'Folder', id: result._id ?? 'root' }]
          : [];

        if (result?.children?.length) {
          result.children.forEach((child) => {
            if (child.type == 'Folder') {
              const folderChildId = typeof child.item === 'string' ? child.item : (child.item as IFolder)._id;

              if (folderChildId) tags.push({ type: 'Folder', id: folderChildId });

            } else if (child.type == 'Project') {
              const projectChildId = typeof child.item === 'string'
                ? child.item
                : (child.item as IProject).projectInfo.id;

              if (projectChildId) tags.push({ type: 'Project', id: projectChildId });
            }
          });
        }
        return tags;
      },
      onQueryStarted: handleApiResult(),
    }),
    addFolder: builder.mutation<void, Omit<IFolder, '_id' | 'path' | 'children' | 'createdAt' | 'updatedAt'>>({
      query: (folder) => ({
        url: '/v1/folders',
        method: 'POST',
        body: folder,
      }),
      invalidatesTags: (_result, _error, { parentFolder }) => {
        const tags: { type: 'Folder'; id: string }[] = [{ type: 'Folder', id: 'LIST' }];

        if (parentFolder) {
          const parentId = typeof parentFolder === 'string' ? parentFolder : parentFolder._id;
          tags.push({ type: 'Folder', id: parentId });
        } else {
          tags.push({ type: 'Folder', id: 'root' });
        }

        return tags;
      },
      onQueryStarted: handleApiResult('Api_Folder_Add_Successful'),
    }),
    updateFolder: builder.mutation<void, Omit<IFolder, 'path' | 'children' | 'createdAt' | 'updatedAt'>>({
      query: ({ _id, ...rest }) => ({
        url: `/v1/folders/${_id}`,
        method: 'PUT',
        body: rest,
      }),
      invalidatesTags: (_result, _error, { _id, parentFolder }) => {
        const tags: ({ type: 'Folder'; id: string } | 'Project')[] = [
          { type: 'Folder', id: 'LIST' },
          { type: 'Folder', id: _id },
          'Project',
        ];

        if (parentFolder) {
          const parentId = typeof parentFolder === 'string' ? parentFolder : parentFolder._id;
          tags.push({ type: 'Folder', id: parentId });
        } else {
          tags.push({ type: 'Folder', id: 'root' });
        }

        return tags;
      },
      onQueryStarted: handleApiResult('Api_Folder_Update_Successful'),
    }),
    deleteFolder: builder.mutation<void, IFolder>({
      query: ({ _id }) => ({
        url: `/v1/folders/${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { _id, parentFolder }) => {
        const tags: ({ type: 'Folder'; id: string } | 'Project')[] = [
          { type: 'Folder', id: 'LIST' },
          { type: 'Folder', id: _id },
          'Project',
        ];

        if (parentFolder) {
          const parentId = typeof parentFolder === 'string' ? parentFolder : parentFolder._id;
          tags.push({ type: 'Folder', id: parentId });
        } else {
          tags.push({ type: 'Folder', id: 'root' });
        }

        return tags;
      },
      onQueryStarted: handleApiResult('Api_Folder_Delete_Successful'),
    }),
  }),
});

export const {
  useGetFoldersQuery,
  useGetFolderQuery,
  useGetFolderByPathQuery,
  useLazyGetFolderByPathQuery,
  useAddFolderMutation,
  useUpdateFolderMutation,
  useDeleteFolderMutation,
} = projectApiSlice;
